import React, { useEffect } from 'react';
import _ from 'lodash';
import { IconButton } from '@material-ui/core';
import PropTypes from 'prop-types';
import DefaultTextField from 'components/Utils/DefaultTextField';
import Icon from 'components/Utils/NewIcon';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import DocumentTemplateUtils from 'services/DocumentTemplateUtils';
import { SmartInputOptions } from 'components/HealthRecords/Documents/DocumentTemplate/SmartInput';
import { DefaultCheckbox, DefaultSwitch as Switch } from 'services/UIUtils';
import DocumentTemplateFields from 'components/HealthRecords/Documents/DocumentTemplate/DocumentTemplateInputs';
import { EDITABLE_BY_ROLES } from './EditableByRolesEnum';
import PlaceholderField from 'components/HealthRecords/Documents/DocumentTemplate/Utils/PlaceholderField';
import { CheckboxPricingFields } from 'components/HealthRecords/Documents/DocumentTemplate/Utils/PricingFields';
import DefaultTooltip from 'components/Utils/DefaultTooltip';
import PerformanceUtils from '../../../../../services/PerformanceUtils';

const SelectedElementEditPanel = (props) => {
  const { selectedEditElement, updateField, copyStyle, pasteStyle, removeField, getEditElement } =
    props;
  if (!selectedEditElement) return null;

  let editElement = getEditElement(selectedEditElement);

  if (!editElement) return null;

  let id = editElement.id || '';
  let type = editElement.type || '';
  let style = editElement.style || '';
  let editableBy = editElement.editable_by || EDITABLE_BY_ROLES.everyone;

  return (
    <div className="row edit-element">
      <span className="small-title">Edit Element</span>
      <div className="icons">
        <Icon
          src="copy"
          alt="Copy Style"
          showTooltip={true}
          className="mr-2"
          height="20px"
          onClick={() => copyStyle(editElement)}
        />
        <Icon
          src="paste"
          alt="Paste Style"
          className="mr-2"
          showTooltip={true}
          height="20px"
          onClick={() => pasteStyle(editElement)}
        />
        <Icon
          showTooltip={true}
          src="trash"
          height="20px"
          className="mr-2"
          onClick={() => removeField(id)}
        />
        <Icon
          showTooltip={true}
          src="xmark"
          height="20px"
          className="mr-2"
          onClick={() => props.selectEditElement(null)}
        />
      </div>
      <DefaultSelectField
        label="Type"
        width="250px"
        disabled={!editElement}
        value={type}
        onChange={(event) => updateField(id, { type: event.target.value })}
        inputs={DocumentTemplateUtils.getInputTypes()}
      />
      <DefaultSelectField
        label="Editable by"
        width="250px"
        disabled={!editElement}
        onChange={(event) => updateField(id, { editable_by: event.target.value })}
        value={editableBy}
        inputs={Object.values(EDITABLE_BY_ROLES)}
      />
      {type === 'checkbox' && <CheckboxOptions element={editElement} updateField={updateField} />}
      {type === 'textarea' && <TextAreaOptions element={editElement} updateField={updateField} />}
      {type === 'textfield' && <TextFieldOptions element={editElement} updateField={updateField} />}
      {type === 'smart' && (
        <SmartInputOptions key={id} {...props} id={id} type={type} editElement={editElement} />
      )}
      {(type === 'stamp' || type === 'patient stamp') && (
        <StampFieldOptions element={editElement} updateField={updateField} />
      )}
      {type === 'signature' && (
        <SignatureOptions
          element={editElement}
          updateField={updateField}
          isPatientSignature={false}
        />
      )}
      {type === 'patient signature' && (
        <SignatureOptions element={editElement} updateField={updateField} isPatientSignature />
      )}
      <DefaultTextField
        label="Style"
        name="style"
        value={style}
        onChange={(event) => updateField(id, { style: event.target.value })}
        disabled={!editElement}
      />
    </div>
  );
};

const SignatureOptions = ({ element, updateField, isPatientSignature }) => {
  const { id, scale } = element;

  useEffect(() => {
    updateField(id, { isPatient: isPatientSignature });
  }, [isPatientSignature]);

  return (
    <>
      <div className="slider" style={{ marginTop: '10px' }}>
        <span style={{ marginRight: '10px' }}>Size</span>
        <input
          type="range"
          min="0.1"
          max="2.0"
          value={scale || 1}
          step="0.1"
          onChange={(event) => {
            let value = event.target.value;
            updateField(id, { scale: value });
          }}
        />
      </div>
    </>
  );
};

const CheckboxOptions = ({ element, updateField }) => {
  const { id, scale, metadata, circle, circle_scale } = element;
  return (
    <>
      <PlaceholderField element={element} updateField={updateField} />
      <SliderInput
        min={0.1}
        max={5.0}
        step={0.1}
        value={scale || 1}
        onValue={(value) => updateField(id, { scale: value })}
        title="Scale"
      />
      <Switch
        label="Circle"
        checked={circle}
        onChange={(event, isInputChecked) => updateField(id, { circle: isInputChecked })}
      />
      {circle && (
        <SliderInput
          min={0.1}
          max={1.0}
          step={0.1}
          value={circle_scale || 1}
          onValue={(value) => updateField(id, { circle_scale: value })}
          title="Circle Shape"
        />
      )}
      <CheckboxPricingFields element={element} updateField={updateField} />
      <DefaultTextField
        label="Metadata"
        name="metadata"
        value={metadata}
        onChange={(event) => updateField(id, { metadata: event.target.value })}
        disabled={!element}
      />
    </>
  );
};

const TextAreaOptions = ({ element, updateField }) => {
  const { id, letter_spacing, line_height, text_indent, font_size = 14 } = element;
  return (
    <>
      <PlaceholderField element={element} updateField={updateField} />
      <SliderInput
        min={0}
        max={20}
        step={0.1}
        value={letter_spacing}
        onValue={(value) => updateField(id, { letter_spacing: value })}
        title="Letter Spacing"
      />
      <SliderInput
        min={15}
        max={50}
        step={0.1}
        value={line_height}
        onValue={(value) => updateField(id, { line_height: value })}
        title="Line Height"
      />
      <SliderInput
        min={0}
        max={300}
        step={1}
        value={text_indent}
        onValue={(value) => updateField(id, { text_indent: value })}
        title="Text Indent"
      />
      <SliderInput
        min={5}
        max={60}
        step={1}
        value={font_size}
        onValue={(value) => updateField(id, { font_size: value })}
        title="Font Size"
      />
    </>
  );
};

const StampFieldOptions = ({ element, updateField }) => {
  const { id, rotation, scale, opacity } = element;
  return (
    <>
      <PlaceholderField element={element} updateField={updateField} />
      <SliderInput
        min={0.1}
        max={2}
        step={0.1}
        value={scale}
        onValue={(value) => updateField(id, { scale: value || 0 })}
        title="Scale"
        defaultValue={1.0}
      />
      <SliderInput
        min={0}
        max={360}
        step={1}
        value={rotation}
        onValue={(value) => updateField(id, { rotation: value || 0 })}
        title="Rotation"
        defaultValue={350}
      />
      <SliderInput
        min={0}
        max={1}
        step={0.05}
        value={opacity}
        onValue={(value) => updateField(id, { opacity: value || 0 })}
        title="Opacity"
        defaultValue={0.8}
      />
    </>
  );
};

const TextFieldOptions = ({ element, updateField }) => {
  const { id, letter_spacing, background, font_size = 14, metadata } = element;
  return (
    <>
      <PlaceholderField element={element} updateField={updateField} />
      <SliderInput
        min={0}
        max={20}
        step={0.1}
        value={letter_spacing}
        onValue={(value) => updateField(id, { letter_spacing: value })}
        title="Letter Spacing"
      />
      <SliderInput
        min={5}
        max={60}
        step={1}
        value={font_size}
        onValue={(value) => updateField(id, { font_size: value })}
        title="Font Size"
      />
      <Checkbox
        title="White Background"
        value={background === 'white'}
        onChange={(value) => updateField(id, { background: value ? 'white' : 'none' })}
      />
      <DefaultTextField
        key={id}
        label="Metadata"
        name="metadata"
        value={metadata}
        onChange={(event) => updateField(id, { metadata: event.target.value })}
        disabled={!element}
      />
    </>
  );
};

const Checkbox = ({ title, value, onChange }) => (
  <div style={{ marginTop: '10px' }}>
    <DefaultCheckbox
      name={title}
      value={value}
      onChange={() => onChange(!value)}
      style={{ display: 'inline-block', margin: 0 }}
      text={title}
    />
  </div>
);

const SliderInput = ({
  min = 0,
  max = 300,
  step = 1,
  onValue,
  title,
  defaultValue = min,
  value = defaultValue,
}) => {
  return (
    <div className="slider" style={{ marginTop: '10px' }}>
      {!!title && <span style={{ marginRight: '10px' }}>{title}</span>}
      <input
        type="range"
        min={'' + min}
        max={'' + max}
        value={'' + (value || defaultValue)}
        step={'' + step}
        onChange={(event) => {
          let value = event.target.value;
          if (value === '0') value = '';
          onValue(value);
        }}
      />
    </div>
  );
};

export const FloatingField = ({
  coords,
  pageNumber,
  prepareNewElement,
  pageWidth,
  pageHeight,
  prefill,
}) => {
  if (!coords) return null;

  let { x, y } = coords;
  let newElement = prepareNewElement(pageNumber, x, y);

  return coords ? (
    <div
      style={{
        pointerEvents: 'none',
        display: 'contents',
      }}
      className="floating-field"
    >
      <DocumentTemplateFields
        editMode={true}
        fieldValues={{}}
        inputs={[newElement]}
        pageWidth={pageWidth}
        pageHeight={pageHeight}
        index={pageNumber}
        onChange={() => {}}
        prefill={prefill}
      />
    </div>
  ) : null;
};

SelectedElementEditPanel.propTypes = {
  selectedEditElement: PropTypes.string,
  selectEditElement: PropTypes.func,
  updateField: PropTypes.func,
  copyStyle: PropTypes.func,
  pasteStyle: PropTypes.func,
  removeField: PropTypes.func,
  getEditElement: PropTypes.func,
};

export const MultipleSelectedElementsPanel = ({
  selectedElements,
  getEditElement,
  onChange,
  templateEdit,
  clearSelection,
}) => {
  // Joining only makes sense for same-type fields sitting on the same page
  // (the classic case: one digit box per character of an OHIP number).
  const selectedInputs = [];
  (templateEdit.fields || []).forEach((page, pageIndex) => {
    (page.inputs || []).forEach((input) => {
      if (input && typeof input === 'object' && selectedElements.has(input.id)) {
        selectedInputs.push({ input, pageIndex });
      }
    });
  });
  const samePage = new Set(selectedInputs.map((entry) => entry.pageIndex)).size === 1;
  const sameType = new Set(selectedInputs.map((entry) => entry.input.type)).size === 1;
  // Checkboxes/stamps/signatures render at a scale-based size that ignores
  // w_percent/h_percent, so a join would change the model without any visible
  // effect — only offer it where the grown box actually renders.
  const joinableType = ['textfield', 'textarea'].includes(selectedInputs[0]?.input.type);
  const canJoin = selectedInputs.length >= 2 && samePage && sameType && joinableType;
  // Measure the rendered boxes rather than trusting w_percent/h_percent:
  // some field types (checkboxes, stamps, signatures) render at a scale-based
  // size that ignores those model values, so aligning on the model would
  // visibly misplace them. Percentages are taken relative to the page box, so
  // this is also unaffected by the zoom transform.
  function measureBoxes(elements) {
    const boxes = [];
    for (const element of elements) {
      const node = document.querySelector(`.template-field[data-field-id="${element.id}"]`);
      const pageNode = node?.closest('.template-page');
      if (!node || !pageNode) continue;
      const rect = node.getBoundingClientRect();
      const pageRect = pageNode.getBoundingClientRect();
      const box = {
        element,
        x: ((rect.left - pageRect.left) / pageRect.width) * 100,
        y: ((rect.top - pageRect.top) / pageRect.height) * 100,
        w: (rect.width / pageRect.width) * 100,
        h: (rect.height / pageRect.height) * 100,
      };
      // The visual box may not start at the model origin (e.g. transform
      // scaling); keep the offset so writing back preserves it.
      box.offsetX = box.x - (parseFloat(element.x_percent) || 0);
      box.offsetY = box.y - (parseFloat(element.y_percent) || 0);
      boxes.push(box);
    }
    return boxes;
  }

  function align(alignment) {
    const myEdit = PerformanceUtils.clone(templateEdit);
    const elements = myEdit.fields
      .flatMap((page) => page.inputs)
      .filter((it) => selectedElements.has(it.id));
    const boxes = measureBoxes(elements);
    if (boxes.length < 2) return;

    const setVisualX = (box, x) => {
      box.element.x_percent = `${x - box.offsetX}%`;
    };
    const setVisualY = (box, y) => {
      box.element.y_percent = `${y - box.offsetY}%`;
    };

    switch (alignment) {
      case 'left': {
        const minX = _(boxes).map('x').min();
        boxes.forEach((box) => setVisualX(box, minX));
        break;
      }
      case 'right': {
        const maxRight = _(boxes)
          .map((box) => box.x + box.w)
          .max();
        boxes.forEach((box) => setVisualX(box, maxRight - box.w));
        break;
      }
      case 'top': {
        const minY = _(boxes).map('y').min();
        boxes.forEach((box) => setVisualY(box, minY));
        break;
      }
      case 'bottom': {
        const maxBottom = _(boxes)
          .map((box) => box.y + box.h)
          .max();
        boxes.forEach((box) => setVisualY(box, maxBottom - box.h));
        break;
      }
      case 'center-h': {
        const minX = _(boxes).map('x').min();
        const maxRight = _(boxes)
          .map((box) => box.x + box.w)
          .max();
        const mid = (minX + maxRight) / 2;
        boxes.forEach((box) => setVisualX(box, mid - box.w / 2));
        break;
      }
      case 'center-v': {
        const minY = _(boxes).map('y').min();
        const maxBottom = _(boxes)
          .map((box) => box.y + box.h)
          .max();
        const mid = (minY + maxBottom) / 2;
        boxes.forEach((box) => setVisualY(box, mid - box.h / 2));
        break;
      }
      case 'distribute-h':
      case 'distribute-v': {
        const isVertical = alignment === 'distribute-v';
        const coord = isVertical ? 'y' : 'x';
        const size = isVertical ? 'h' : 'w';
        const setVisual = isVertical ? setVisualY : setVisualX;
        if (boxes.length < 3) return;

        // The outermost boxes stay put; the rest are spaced evenly between
        // the first box's far edge and the last box's near edge. When a single
        // box spans both extremes (e.g. right after centering), distribute the
        // others within that box's span instead.
        const first = _.minBy(boxes, (box) => box[coord]);
        const last = _.maxBy(boxes, (box) => box[coord] + box[size]);
        const spanning = first === last;

        const middles = _.sortBy(
          boxes.filter((box) => box !== first && box !== last),
          (box) => box[coord],
        );
        const minBound = spanning ? first[coord] : first[coord] + first[size];
        const maxBound = spanning ? first[coord] + first[size] : last[coord];
        const covered = _(middles).map(size).sum();
        const gap = (maxBound - minBound - covered) / (middles.length + 1);

        let base = minBound + gap;
        for (const box of middles) {
          setVisual(box, base);
          base += box[size] + gap;
        }
        break;
      }
      default: {
        return;
      }
    }
    onChange('templateEdit', myEdit);
  }

  // Merge the selected fields into one: the top-left-most survives, grown to
  // the bounding box of the whole selection, with its metadata tag cleared
  // (per-box tags like "digit 3" are meaningless on the joined field).
  function joinFields() {
    const myEdit = PerformanceUtils.clone(templateEdit);
    const elements = myEdit.fields
      .flatMap((page) => page.inputs)
      .filter((it) => it && typeof it === 'object' && selectedElements.has(it.id));
    const boxes = measureBoxes(elements);
    if (boxes.length < 2 || boxes.length !== elements.length) return;

    const minX = _(boxes).map('x').min();
    const minY = _(boxes).map('y').min();
    const maxRight = _(boxes)
      .map((box) => box.x + box.w)
      .max();
    const maxBottom = _(boxes)
      .map((box) => box.y + box.h)
      .max();

    const base = _.minBy(boxes, (box) => box.x + box.y);
    base.element.x_percent = `${minX - base.offsetX}%`;
    base.element.y_percent = `${minY - base.offsetY}%`;
    base.element.w_percent = `${maxRight - minX}%`;
    base.element.h_percent = `${maxBottom - minY}%`;
    delete base.element.metadata;

    const removeIds = new Set(
      elements.filter((element) => element !== base.element).map((element) => element.id),
    );
    myEdit.fields.forEach((page) => {
      page.inputs = (page.inputs || []).filter((input) => !(input && removeIds.has(input.id)));
    });

    onChange('templateEdit', myEdit);
    clearSelection?.();
  }

  // Same behavior as pressing Delete with a selection — no confirm, and Cancel
  // still discards it along with any other unsaved edit.
  function deleteSelected() {
    const myEdit = PerformanceUtils.clone(templateEdit);
    myEdit.fields.forEach((page) => {
      page.inputs = (page.inputs || []).filter(
        (input) => !(input && selectedElements.has(input.id)),
      );
    });
    onChange('templateEdit', myEdit);
    clearSelection?.();
  }

  const count = selectedElements.size;
  const canDistribute = count >= 3;

  return (
    <div className="row edit-element">
      <span className="small-title">Selected Elements ({count})</span>
      <div className="section-alignment flex flex-wrap gap-4 justify-center">
        <div className="flex gap-1">
          <SelectionToolButton
            tooltip="Align left edges"
            icon="objects-align-left"
            onClick={() => align('left')}
          />
          <SelectionToolButton
            tooltip="Center horizontally"
            icon="objects-align-center-horizontal"
            onClick={() => align('center-h')}
          />
          <SelectionToolButton
            tooltip="Align right edges"
            icon="objects-align-right"
            onClick={() => align('right')}
          />
        </div>
        <div className="flex gap-1">
          <SelectionToolButton
            tooltip="Align top edges"
            icon="objects-align-top"
            onClick={() => align('top')}
          />
          <SelectionToolButton
            tooltip="Center vertically"
            icon="objects-align-center-vertical"
            onClick={() => align('center-v')}
          />
          <SelectionToolButton
            tooltip="Align bottom edges"
            icon="objects-align-bottom"
            onClick={() => align('bottom')}
          />
        </div>
        <div className="flex gap-1">
          <SelectionToolButton
            tooltip={
              canDistribute
                ? 'Distribute horizontal spacing'
                : 'Select at least 3 elements to distribute'
            }
            icon="distribute-spacing-horizontal"
            disabled={!canDistribute}
            onClick={() => align('distribute-h')}
          />
          <SelectionToolButton
            tooltip={
              canDistribute
                ? 'Distribute vertical spacing'
                : 'Select at least 3 elements to distribute'
            }
            icon="distribute-spacing-vertical"
            disabled={!canDistribute}
            onClick={() => align('distribute-v')}
          />
        </div>
        <div className="flex gap-1">
          <SelectionToolButton
            tooltip={
              canJoin
                ? `Join the ${count} selected fields into one spanning their combined area`
                : 'Join fields: select two or more text fields of the same type on the same page'
            }
            icon="object-union"
            disabled={!canJoin}
            onClick={joinFields}
          />
          <SelectionToolButton
            tooltip={`Delete the ${count} selected fields`}
            icon="trash"
            iconClassName="red"
            onClick={deleteSelected}
          />
        </div>
      </div>
    </div>
  );
};

/**
 * Alignment-row icon button with the portal tooltip. The span wrapper keeps
 * the tooltip working when the button is disabled (MUI buttons swallow hover
 * events while disabled).
 */
const SelectionToolButton = ({ tooltip, icon, onClick, disabled = false, iconClassName }) => (
  <DefaultTooltip placement="top" title={tooltip}>
    <span style={{ display: 'inline-flex' }}>
      <IconButton size="small" disabled={disabled} onClick={onClick}>
        <Icon src={icon} className={iconClassName} />
      </IconButton>
    </span>
  </DefaultTooltip>
);

export default SelectedElementEditPanel;
